import { ClockIcon } from '@heroicons/react/20/solid'
import moment from 'moment'
import TrainingItemMonth from './TrainingItemMonth'

const normalizeAvailabilitySlotLabel = slots => {
	const normalizedSlots = Array.isArray(slots) ? slots.filter(Boolean) : []
	if (normalizedSlots.includes('8-14') && normalizedSlots.includes('14-20')) {
		return '8-20'
	}

	return normalizedSlots.join(', ')
}

const getAvailabilityBlockStyle = slots => {
	const normalizedSlots = Array.isArray(slots) ? slots : []
	const hasFirstHalf = normalizedSlots.includes('8-14')
	const hasSecondHalf = normalizedSlots.includes('14-20')

	if (hasFirstHalf && hasSecondHalf) {
		return {
			block: 'bg-emerald-100 text-emerald-900',
			selected: 'bg-emerald-200 ring-1 ring-emerald-500',
			checkbox: 'border-emerald-400 text-emerald-700 focus:ring-emerald-500',
		}
	}

	if (hasFirstHalf) {
		return {
			block: 'bg-amber-100 text-amber-900',
			selected: 'bg-amber-200 ring-1 ring-amber-500',
			checkbox: 'border-amber-400 text-amber-700 focus:ring-amber-500',
		}
	}

	return {
		block: 'bg-violet-100 text-violet-900',
		selected: 'bg-violet-200 ring-1 ring-violet-500',
		checkbox: 'border-violet-400 text-violet-700 focus:ring-violet-500',
	}
}

const getTrainingRangeForWeek = (training, week) => {
	const weekStart = moment(week[0]?.date, 'YYYY-MM-DD', true)
	const weekEnd = moment(week[week.length - 1]?.date, 'YYYY-MM-DD', true)
	const trainingStart = moment(training?.trainingDate, moment.ISO_8601, true)
	const trainingEnd = moment(training?.trainingEndDate || training?.trainingDate, moment.ISO_8601, true)

	if (!weekStart.isValid() || !weekEnd.isValid() || !trainingStart.isValid() || !trainingEnd.isValid()) {
		return null
	}

	const visibleStart = moment.max(trainingStart, weekStart)
	const visibleEnd = moment.min(trainingEnd, weekEnd)
	if (visibleEnd.isBefore(visibleStart, 'day')) {
		return null
	}

	return {
		training,
		visibleStart,
		visibleEnd,
	}
}

const buildTrainingTracks = (week, trainings = []) => {
	const weekTrainings = trainings
		.map(training => getTrainingRangeForWeek(training, week))
		.filter(Boolean)
		.sort((left, right) => {
			const leftDate = left.visibleStart.format('YYYY-MM-DD')
			const rightDate = right.visibleStart.format('YYYY-MM-DD')
			if (leftDate !== rightDate) {
				return leftDate.localeCompare(rightDate, 'pl')
			}

			return `${left.training?.trainingName || ''}`.localeCompare(`${right.training?.trainingName || ''}`, 'pl')
		})

	const trackEndDates = []

	return weekTrainings.map(item => {
		const availableTrackIndex = trackEndDates.findIndex(trackEnd =>
			item.visibleStart.isAfter(trackEnd, 'day')
		)

		const trackIndex = availableTrackIndex === -1 ? trackEndDates.length : availableTrackIndex
		trackEndDates[trackIndex] = item.visibleEnd.clone()

		return {
			...item,
			trackIndex,
		}
	})
}

const getTrainingInsetClass = ({ isSingleDay, isRangeStart, isRangeEnd }) => {
	if (isSingleDay || (isRangeStart && isRangeEnd)) {
		return 'px-2'
	}

	if (isRangeStart) {
		return 'pl-2 pr-0'
	}

	if (isRangeEnd) {
		return 'pl-0 pr-2'
	}

	return 'px-0'
}

const groupTracks = tracks => tracks.reduce((acc, item) => {
	acc[item.trackIndex] = [...(acc[item.trackIndex] || []), item]
	return acc
}, [])

function RangeTracks({ groupedTracks, week, top, rowHeight, tone, onItemClick }) {
	if (!groupedTracks.length) return null

	return (
		<div className="pointer-events-none absolute inset-x-0" style={{ top: `${top}px` }}>
			{groupedTracks.map((trackItems, trackIndex) => (
				<div
					key={`range-track-row-${tone}-${trackIndex}`}
					className="grid grid-cols-7"
					style={{ height: `${rowHeight}px` }}>
					{trackItems.map(track => {
						const startColumn = week.findIndex(day => moment(day.date, 'YYYY-MM-DD', true).isSame(track.visibleStart, 'day')) + 1
						const endColumn = week.findIndex(day => moment(day.date, 'YYYY-MM-DD', true).isSame(track.visibleEnd, 'day')) + 1
						const span = Math.max(endColumn - startColumn + 1, 1)
						const itemStart = moment(track.training?.trainingDate, moment.ISO_8601, true)
						const itemEnd = moment(track.training?.trainingEndDate || track.training?.trainingDate, moment.ISO_8601, true)
						const isSingleDay = track.visibleStart.isSame(track.visibleEnd, 'day')
						const isRangeStart = track.visibleStart.isSame(itemStart, 'day')
						const isRangeEnd = track.visibleEnd.isSame(itemEnd, 'day')
						const insetClass = getTrainingInsetClass({ isSingleDay, isRangeStart, isRangeEnd })

						return (
							<div
								key={String(track.training?._id || track.training?.id || `range-track-${tone}-${track.trackIndex}`)}
								className={`pointer-events-auto cursor-pointer ${insetClass}`}
								style={{ gridColumn: `${startColumn} / span ${span}` }}
								onClick={event => {
									event.stopPropagation()
									onItemClick?.(track.training)
								}}>
								<TrainingItemMonth
									title={track.training?.trainingName || '—'}
									isSingleDay={isSingleDay}
									isRangeStart={isRangeStart}
									isRangeEnd={isRangeEnd}
									tone={tone}
								/>
							</div>
						)
					})}
				</div>
			))}
		</div>
	)
}

function MonthWeekRow({
	week,
	trainings,
	projects = [],
	availability,
	selectedIds = [],
	onAvailabilityClick,
	onEmptyDayClick,
	onToggleSelect,
	onTrainingClick,
	onProjectClick,
	selectLabel,
}) {
	const trainingTracks = buildTrainingTracks(week, trainings)
	const groupedTrainingTracks = groupTracks(trainingTracks)
	const groupedProjectTracks = groupTracks(buildTrainingTracks(week, projects))

	const getAvailabilityForDate = date =>
		(availability || []).filter(item => {
			const itemStart = moment(item?.startDate || item?.dateFrom)
			const itemEnd = moment(item?.endDate || item?.dateTo)
			const currentDay = moment(date, 'YYYY-MM-DD', true)

			if (!itemStart.isValid() || !itemEnd.isValid() || !currentDay.isValid()) return false
			return !currentDay.isBefore(itemStart, 'day') && !currentDay.isAfter(itemEnd, 'day')
		})

	const availabilityByDate = week.map(day => {
		const items = getAvailabilityForDate(day.date)
			.map(item => ({
				label: normalizeAvailabilitySlotLabel(item?.slots),
				item,
			}))
			.filter(entry => entry.label)
		return { date: day.date, items }
	})
	const availabilityRowCount = Math.max(1, ...availabilityByDate.map(entry => entry.items.length))
	const availabilityAreaTop = 30
	const availabilityRowHeight = 24
	const trainingRowHeight = 28
	const trainingAreaTop = availabilityAreaTop + availabilityRowHeight * availabilityRowCount + 8
	const minWeekHeight = 128
	const projectAreaTop = trainingAreaTop + groupedTrainingTracks.length * trainingRowHeight
	const reservedRows = Math.max(groupedTrainingTracks.length + groupedProjectTracks.length, 1)
	const minCellHeight = Math.max(
		minWeekHeight,
		trainingAreaTop + reservedRows * trainingRowHeight + 12
	)

	return (
		<div className="relative grid grid-cols-7">
			{week.map(day => {
				const availabilityItems = availabilityByDate.find(entry => entry.date === day.date)?.items || []
				const isToday = moment(day.date, 'YYYY-MM-DD', true).isSame(moment(), 'day')
				const isEmptyDay = availabilityItems.length === 0

				return (
					<div
						key={day.date}
						style={{ minHeight: `${minCellHeight}px` }}
						onClick={() => {
							if (isEmptyDay) onEmptyDayClick(day.date)
						}}
						className={`relative overflow-visible border-r border-gray-200 p-2 last:border-r-0 ${
							isEmptyDay ? 'cursor-pointer hover:bg-indigo-50' : ''
						} ${isToday ? 'bg-gray-100' : !day.isCurrentMonth ? 'bg-gray-50' : ''}`}>
						<div
							className={`mb-1 text-sm font-medium ${
								isToday ? 'text-gray-950' : day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
							}`}>
							{day.day}
						</div>

						<div className="space-y-1">
							{availabilityItems.map(({ label, item }, index) => {
								const availabilityId = `${item?._id || item?.id || ''}`
								const isSelected = selectedIds.includes(availabilityId)
								const blockStyle = getAvailabilityBlockStyle(item?.slots)
								return (
									<div
										key={`${availabilityId}-${label}-${index}`}
										className={`flex items-center gap-1 rounded px-1.5 py-1 text-left text-xs font-medium ${
											isSelected ? blockStyle.selected : blockStyle.block
										}`}>
										<input
											type="checkbox"
											checked={isSelected}
											aria-label={selectLabel}
											onClick={event => event.stopPropagation()}
											onChange={() => onToggleSelect(availabilityId)}
											className={`h-3.5 w-3.5 shrink-0 rounded ${blockStyle.checkbox}`}
										/>
										<button
											type="button"
											onClick={event => {
												event.stopPropagation()
												onAvailabilityClick(item)
											}}
											className="flex min-w-0 flex-1 cursor-pointer items-center text-left font-semibold">
											<ClockIcon className="mr-1 size-3.5 shrink-0" aria-hidden="true" />
											<span className="min-w-0 truncate">{label}</span>
										</button>
									</div>
								)
							})}

							{Array.from({ length: Math.max(availabilityRowCount - availabilityItems.length, 0) }).map((_, index) => (
								<div key={`availability-spacer-${day.date}-${index}`} className="h-6" />
							))}
						</div>
					</div>
				)
			})}

			<RangeTracks
				groupedTracks={groupedTrainingTracks}
				week={week}
				top={trainingAreaTop}
				rowHeight={trainingRowHeight}
				tone="emerald"
				onItemClick={onTrainingClick}
			/>
			<RangeTracks
				groupedTracks={groupedProjectTracks}
				week={week}
				top={projectAreaTop}
				rowHeight={trainingRowHeight}
				tone="indigo"
				onItemClick={onProjectClick}
			/>
		</div>
	)
}

export default MonthWeekRow
